import { useEffect, useState, useMemo, useRef } from 'react';
import { userAppContext } from '../../../../../Context/Context.jsx';

import TableColumnsMenu from '../menu/TableColumnsMenu';
import { EVIDENCE_LEGEND } from '../../constants';

import RenderInfoLogic from '../../../../../Components/InfoSidebar/RenderInfoLogic.jsx';
import Grid from '../../../../../Components/Table/Grid.jsx';
import { Columns } from '../../../../../Components/Table/Columns.jsx';
import { ColumnVisibilityModels } from '../../../../../Components/Table/ColumnVisibilityModels';

import RenderSourcesLogic from '../source/RenderSourcesLogic';

import SharedButton from '../../../../../global/Button/Button';
import { ViewColumnsIcon } from '@heroicons/react/16/solid';
import {
  ArrowDownTrayIcon,
  BookmarkIcon,
  ChevronDownIcon,
  RectangleStackIcon,
  FunnelIcon,
  TableCellsIcon,
} from '@heroicons/react/16/solid';

import { DOWNLOAD_EXTENSIONS } from '../../constants';
import { fetchDownloadFiltersFile } from '../../apiCalls';

import { ColumnVisibility, ExtensionFile, UserContext } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';
import Alert from '../../../../../global/Alert/Alert';
import { isSourceEnabledForTab } from '../../helpers/sourceListHelpers';
import ErrorMessage from '../../../../../Components/UI/ErrorMessage.jsx';
import { EvidenceParams, evidenceSummaryRequest } from '../../helpers/tableHelpers';

import { buildFiltersObject } from '../../helpers/buildFiltersObject';
import BaseTooltip from '../../../../../global/Tooltip/BaseTooltip';
import { saveFilter, deleteFilter } from '../../../../../services/savedFiltersService';
import { useLocation } from 'react-router-dom';
import SavedColumnsBar from '../../../../../Components/UI/SavedColumnsBar';

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';

function ManageGrid(props) {
  const {
    isMapping,
    mappingContent,
    restrictChemical,
    view,
    tab,
    isEvidence,
    source,
    searchItems,
    setSearchItems,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    dispatch,
    defaultFilterValue,
    sourceWeights = [],
    onScoreSaved,
  } = props;

  const sourceCurated = ['CURATED', 'CLINGEN', 'CLINPGX', 'CLINVAR', 'GWASCAT', 'PHEWASCAT', 'UNIPROT'];

  const { authorization, isAuthenticated, selectedInfoItem, restrictSources, user } =
    userAppContext() as unknown as UserContext;

  const hasLoadedFromSavedRef = useRef(false);

  const useCustomColumnVisibility = () => {
    const selectColumnVisibilityModel = useMemo(() => {
      const fnColumnVisibilityModel =
        isMapping && mappingContent?.isActive
          ? () => {
              const model = ColumnVisibilityModels.getByMappingName(
                mappingContent.columVisibilityModelKey,
                isEvidence,
              );
              return model;
            }
          : () => {
              return ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
            };

      const fnColumnVisibilityModelWithRestrictions = () => {
        const originalList = fnColumnVisibilityModel();
        const restrictedList = restrictChemical
          ? ['gof_lof', 'chemsInSentence', 'chemID', 'chemIDs', 'chemical_effect']
          : [];
        return originalList && restrictedList
          ? Object.fromEntries(Object.entries(originalList).filter(([key]) => !restrictedList.includes(key)))
          : undefined;
      };

      return fnColumnVisibilityModelWithRestrictions;
    }, [view, tab, mappingContent?.columVisibilityModelKey, restrictChemical, isMapping, isEvidence]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
      return selectColumnVisibilityModel();
    });

    const [columnOrder, setColumnOrder] = useState<string[] | undefined>(undefined);

    useEffect(() => {
      const newBaseModel = selectColumnVisibilityModel();
      setColumnVisibilityModel(newBaseModel);

      if (newBaseModel) {
        const orderedKeys = Object.keys(newBaseModel);
        const visibleKeys = orderedKeys.filter(key => newBaseModel[key] === true);
        setColumnOrder(visibleKeys);
      }

      hasLoadedFromSavedRef.current = false;
    }, [selectColumnVisibilityModel, view, tab, isEvidence]);

    useEffect(() => {
      const baseModel = selectColumnVisibilityModel();
      if (baseModel) {
        const orderedKeys = Object.keys(baseModel);
        const visibleKeys = orderedKeys.filter(key => baseModel[key] === true);
        setColumnOrder(visibleKeys);
      } else {
        setColumnOrder(undefined);
      }
    }, [tab, view, isEvidence, selectColumnVisibilityModel]);

    return {
      columnVisibilityModel,
      setColumnVisibilityModel,
      columnOrder,
      setColumnOrder,
    };
  };

  const { columnVisibilityModel, setColumnVisibilityModel, columnOrder, setColumnOrder } =
    useCustomColumnVisibility();

  const [totalResults, setTotalResults] = useState(0);
  const [isColumnSelectOpen, setIsColumnSelectOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('tableColumnOpen');
    return saved === 'true';
  });
  const [sourceOpen, setSourceOpen] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [pendingDownloadExt, setPendingDownloadExt] = useState<ExtensionFile | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<'info' | 'success' | 'error' | 'warning'>('success');
  const [customAlertMessage, setCustomAlertMessage] = useState<string | null>(null);
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);
  const isAcademic = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'][0] === 'ROLE_ACADEMIC';
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');

  const [areEvidenceSelectedRows, setEvidenceSelectedRows] = useState(false);
  const [evidenceSummParams, setEvidenceSummParams] = useState<EvidenceParams[]>([]);
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [showSavedColumns, setShowSavedColumns] = useState(false);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchDataToSave, setSearchDataToSave] = useState<any>(null);
  const location = useLocation();

  const [showSaveColumnsModal, setShowSaveColumnsModal] = useState(false);
  const [columnSelectionName, setColumnSelectionName] = useState('');
  const [columnDataToSave, setColumnDataToSave] = useState<any>(null);
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [showSaveScoreModal, setShowSaveScoreModal] = useState(false);
  const [scoreName, setScoreName] = useState('');
  const [isSavingScore, setIsSavingScore] = useState(false);
  const saveDropdownRef = useRef<HTMLDivElement>(null);
  const [refreshColumnsBar, setRefreshColumnsBar] = useState(0);
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const loadColumnsId = urlParams.get('loadColumns');
    const loadCompleteId = urlParams.get('loadComplete');

    const loadConfigurationFromDynamoDB = async (configId: string, isComplete: boolean) => {
      setIsLoadingConfiguration(true);
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { getFilterById } = await import('../../../../../services/savedFiltersService');
        const configData = await getFilterById(configId);

        if (configData) {
          if (configData.columnVisibilityModel) {
            hasLoadedFromSavedRef.current = true;
            setColumnVisibilityModel(configData.columnVisibilityModel);
          }
          if (configData.columnOrder) {
            setColumnOrder(configData.columnOrder);
          }

          setAlertType('success');
          setCustomAlertMessage(
            `${isComplete ? 'Complete search' : 'Column configuration'} "${configData.name}" applied successfully`
          );
          setShowAlert(true);

          urlParams.delete(isComplete ? 'loadComplete' : 'loadColumns');
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState({}, '', newUrl);
        } else {
          setAlertType('error');
          setCustomAlertMessage(`${isComplete ? 'Complete search' : 'Column configuration'} not found`);
          setShowAlert(true);
        }
      } catch (error) {
        console.error(`Error loading ${isComplete ? 'complete search' : 'column configuration'}:`, error);
        setAlertType('error');
        setCustomAlertMessage(`Error loading ${isComplete ? 'complete search' : 'column configuration'}`);
        setShowAlert(true);
      } finally {
        setIsLoadingConfiguration(false);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    if (loadCompleteId) {
      if (loadCompleteId.startsWith('completeSearch_')) {
        try {
          const searchData = localStorage.getItem(loadCompleteId);
          if (searchData) {
            const search = JSON.parse(searchData);

            if (search.columnVisibilityModel) {
              hasLoadedFromSavedRef.current = true;
              setColumnVisibilityModel(search.columnVisibilityModel);
            }
            if (search.columnOrder) {
              setColumnOrder(search.columnOrder);
            }

            setAlertType('success');
            setCustomAlertMessage(`Complete search "${search.name}" applied successfully`);
            setShowAlert(true);

            urlParams.delete('loadComplete');
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
          } else {
            setAlertType('error');
            setCustomAlertMessage('Complete search not found');
            setShowAlert(true);
          }
        } catch (error) {
          console.error('Error loading legacy complete search:', error);
          setAlertType('error');
          setCustomAlertMessage('Error loading complete search');
          setShowAlert(true);
        }
      } else {
        // Load from DynamoDB
        loadConfigurationFromDynamoDB(loadCompleteId, true);
      }
    } else if (loadColumnsId) {
      if (loadColumnsId.startsWith('columnSelection_')) {
        try {
          const selectionData = localStorage.getItem(loadColumnsId);
          if (selectionData) {
            const selection = JSON.parse(selectionData);
            
            if (selection.columnVisibilityModel) {
              hasLoadedFromSavedRef.current = true;
              setColumnVisibilityModel(selection.columnVisibilityModel);
            }
            if (selection.columnOrder) {
              setColumnOrder(selection.columnOrder);
            }

            setAlertType('success');
            setCustomAlertMessage(`Columns "${selection.name}" applied successfully`);
            setShowAlert(true);

            urlParams.delete('loadColumns');
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
          } else {
            setAlertType('error');
            setCustomAlertMessage('Column configuration not found');
            setShowAlert(true);
          }
        } catch (error) {
          console.error('Error loading legacy column configuration:', error);
          setAlertType('error');
          setCustomAlertMessage('Error loading column configuration');
          setShowAlert(true);
        }
      } else {
        loadConfigurationFromDynamoDB(loadColumnsId, false);
      }
    }
  }, [location.search, setColumnVisibilityModel, setColumnOrder, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(event.target as Node)) {
        setShowSaveDropdown(false);
      }
    };

    if (showSaveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveDropdown]);

  const handleOpenMenus = (menu: string) => {
    setIsColumnSelectOpen(prev => (menu === 'columns' ? !prev : false));
    setSourceOpen(prev => (menu === 'source' ? !prev : false));
  };

  useEffect(() => {
    localStorage.setItem('tableColumnOpen', isColumnSelectOpen ? 'true' : 'false');
  }, [isColumnSelectOpen, tab]);

  const handleDataFromChild = (data: boolean, params: EvidenceParams[]) => {
    setEvidenceSelectedRows(data);
    setEvidenceSummParams(params);
  };

  const handleEvidenceSummClick = async () => {
    const content = await evidenceSummaryRequest(evidenceSummParams, dispatch);
    dispatch({
      type: 'SET_EVIDENCE_MODAL_DATA',
      payload: evidenceSummParams,
    });
    await dispatch({ type: 'SET_EVIDENCE_MODAL_CONTENT', payload: content });
    dispatch({ type: 'SET_SHOW_EVIDENCE_MODAL', payload: true });
  };

  const getFilterValueRef = useRef<any>(null);

  const alertMessage = () => {
    if (customAlertMessage) return customAlertMessage;

    if (alertType === 'success') {
      if (isSavingSearch) return 'Search saved successfully!';
      if (isSavingColumns) return 'Column selection saved successfully!';
      if (showSavedColumns) return 'Column selection loaded successfully!';
      if (isSavingSearch) return 'Search saved successfully!';
      if (isSavingColumns) return 'Column selection saved successfully!';
      if (showSavedColumns) return 'Column selection loaded successfully!';
      return 'Successfully downloaded!';
    }
    if (alertType === 'error') {
      if (isSavingSearch) return 'Error saving search. Please try again.';
      if (isSavingColumns) return 'Error saving column selection. Please try again.';
      if (isSavingSearch) return 'Error saving search. Please try again.';
      if (isSavingColumns) return 'Error saving column selection. Please try again.';
      return 'Error downloading file.';
    }
    if (alertType === 'warning') {
      return 'Download is being processed.';
    }
  };

  const handleSaveSearch = () => {
    if (!isAuthenticated) {
      setAlertType('error');
      setShowAlert(true);
      return;
    }

    const allFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];

    const contextParams = ['view', 'tab', 'source', 'idents'];
    const columnFilters = allFilters.filter(
      f =>
        !contextParams.includes(f.name) &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
    );

    const urlParams = new URLSearchParams(location.search);
    const searchQuery = `view=${view}&tab=${tab}&source=${source}&idents=${urlParams.get('idents') || 'ALL'}&isEvidence=${isEvidence}`;

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Search';

    const defaultSearchName = `${searchItemsText} - ${titleGrid} (${new Date().toLocaleDateString()})`;

    setSearchDataToSave({
      columnFilters,
      searchQuery,
      isEvidence,
      searchItems: searchItems || [],
      view,
      tab,
      source,
      totalResults,
      saveType: 'filters',
    });
    setSearchName(defaultSearchName);

    setShowSaveSearchModal(true);
    setShowSaveDropdown(false);
  };

  const handleConfirmSaveSearch = async () => {
    if (!searchDataToSave || !searchName.trim() || isSavingSearch) return;

    setIsSavingSearch(true);

    try {
      if (searchDataToSave.saveType === 'complete') {
        try {
          const { saveCompleteSearch } = await import('../../../../../services/savedFiltersService');

          await saveCompleteSearch(
            searchName.trim(),
            searchDataToSave.columnFilters,
            searchDataToSave.searchQuery,
            searchDataToSave.columnVisibilityModel,
            searchDataToSave.columnOrder,
            {
              view: searchDataToSave.view,
              tab: searchDataToSave.tab,
              source: searchDataToSave.source,
              isEvidence: searchDataToSave.isEvidence,
              searchItems: searchDataToSave.searchItems,
            },
          );
        } catch (dbError) {
          console.warn('⚠️ DynamoDB save failed, falling back to localStorage:', dbError);

          const timestamp = new Date().getTime();
          const completeSearchKey = `completeSearch_${timestamp}`;

          const completeSearch = {
            name: searchName.trim(),
            columnFilters: searchDataToSave.columnFilters,
            searchQuery: searchDataToSave.searchQuery,
            columnVisibilityModel: searchDataToSave.columnVisibilityModel,
            columnOrder: searchDataToSave.columnOrder,
            savedAt: new Date().toISOString(),
            searchContext: {
              view: searchDataToSave.view,
              tab: searchDataToSave.tab,
              source: searchDataToSave.source,
              isEvidence: searchDataToSave.isEvidence,
              searchItems: searchDataToSave.searchItems,
            },
          };

          localStorage.setItem(completeSearchKey, JSON.stringify(completeSearch));
        }
      } else {
        await saveFilter(
          searchName.trim(),
          searchDataToSave.columnFilters,
          searchDataToSave.isEvidence,
          searchDataToSave.searchQuery,
        );
      }

      setAlertType('success');
      setCustomAlertMessage(
        searchDataToSave.saveType === 'complete'
          ? 'Complete search saved successfully!'
          : 'Search saved successfully!',
      );
      setShowAlert(true);
      setShowSaveSearchModal(false);
      setSearchDataToSave(null);
      setSearchName('');

      setTimeout(() => {
        setIsSavingSearch(false);
      }, 100);
    } catch (error) {
      console.error('Error saving search:', error);
      setAlertType('error');
      setCustomAlertMessage('Error saving search. Please try again.');
      setShowAlert(true);
    } finally {
      setIsSavingSearch(false);
    }
  };

  const handleSaveColumnSelection = () => {
    if (!columnVisibilityModel) return;

    const defaultColumnName = `${view} - ${tab} ${isEvidence ? '(Evidence)' : '(Summary)'} - Columns (${new Date().toLocaleDateString()})`;

    const hasActiveSearch = searchItems && searchItems.length > 0 && searchItems[0]?.id !== 'ALL';

    setColumnDataToSave({
      columnVisibilityModel,
      columnOrder,
      view,
      tab,
      isEvidence,
      searchItems: hasActiveSearch ? searchItems?.map(item => ({ id: item.id, name: item.name })) : undefined,
    });
    setColumnSelectionName(defaultColumnName);
    setShowSaveColumnsModal(true);
    setShowSaveDropdown(false);
  };

  const handleSaveComplete = () => {
    if (!isAuthenticated) {
      setAlertType('error');
      setCustomAlertMessage('You must be logged in to save');
      setShowAlert(true);
      return;
    }

    const allFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];

    const contextParams = ['view', 'tab', 'source', 'idents'];
    const columnFilters = allFilters.filter(
      f =>
        !contextParams.includes(f.name) &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
    );

    const urlParams = new URLSearchParams(location.search);
    const searchQuery = `view=${view}&tab=${tab}&source=${source}&idents=${urlParams.get('idents') || 'ALL'}&isEvidence=${isEvidence}`;

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Search';

    const defaultSearchName = `${searchItemsText} - ${titleGrid} (${new Date().toLocaleDateString()})`;

    setSearchDataToSave({
      columnFilters,
      searchQuery,
      isEvidence,
      searchItems: searchItems || [],
      view,
      tab,
      source,
      totalResults,
      columnVisibilityModel,
      columnOrder,
      saveType: 'complete',
    });
    setSearchName(defaultSearchName);
    setShowSaveSearchModal(true);
    setShowSaveDropdown(false);
  };

  const handleResetToDefault = () => {
    const defaultModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
    if (defaultModel) {
      setColumnVisibilityModel(defaultModel);

      const defaultColumnOrder = Object.keys(defaultModel).filter(key => defaultModel[key] === true);
      setColumnOrder(defaultColumnOrder);
    }

    hasLoadedFromSavedRef.current = false; // Allow default model updates again

    setAlertType('success');
    setCustomAlertMessage('Columns reset to default');
    setShowAlert(true);
  };

  const handleConfirmSaveColumnSelection = async () => {
    if (!columnDataToSave || !columnSelectionName.trim() || isSavingColumns) return;

    setIsSavingColumns(true);

    try {
      try {
        const { saveColumnSelection } = await import('../../../../../services/savedFiltersService');

        await saveColumnSelection(
          columnSelectionName.trim(),
          columnDataToSave.columnVisibilityModel,
          columnDataToSave.columnOrder,
          {
            view: columnDataToSave.view,
            tab: columnDataToSave.tab,
            isEvidence: columnDataToSave.isEvidence,
            searchItems: columnDataToSave.searchItems,
          },
        );
      } catch (dbError) {
        console.warn('⚠️ DynamoDB save failed, falling back to localStorage:', dbError);

        const timestamp = new Date().getTime();
        const columnSelectionKey = `columnSelection_${timestamp}`;

        const columnSelection = {
          name: columnSelectionName.trim(),
          columnVisibilityModel: columnDataToSave.columnVisibilityModel,
          columnOrder: columnDataToSave.columnOrder,
          savedAt: new Date().toISOString(),
          searchContext: {
            view: columnDataToSave.view,
            tab: columnDataToSave.tab,
            isEvidence: columnDataToSave.isEvidence,
            searchItems: columnDataToSave.searchItems,
          },
        };

        localStorage.setItem(columnSelectionKey, JSON.stringify(columnSelection));
      }

      setAlertType('success');
      setCustomAlertMessage('Column selection saved successfully!');
      setShowAlert(true);
      setShowSaveColumnsModal(false);
      setColumnDataToSave(null);
      setColumnSelectionName('');
      setRefreshColumnsBar(prev => prev + 1);

      setTimeout(() => {
        setIsSavingColumns(false);
      }, 100);
    } catch (error) {
      console.error('Error saving column selection:', error);
      setAlertType('error');
      setCustomAlertMessage('Error saving column selection. Please try again.');
      setShowAlert(true);
      setIsSavingColumns(false);
    }
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      const baseModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

      if (baseModel) {
        const mergedModel = { ...baseModel };

        Object.keys(savedColumn.columnVisibilityModel).forEach(key => {
          if (key in mergedModel) {
            mergedModel[key] = savedColumn.columnVisibilityModel[key];
          }
        });

        setColumnVisibilityModel(mergedModel);
      } else {
        setColumnVisibilityModel(savedColumn.columnVisibilityModel);
      }
    }

    if (savedColumn.columnOrder) {
      setColumnOrder(savedColumn.columnOrder);
    }

    setAlertType('success');
    setCustomAlertMessage(`Column configuration "${savedColumn.name}" applied successfully`);
    setShowAlert(true);
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) {
        await deleteFilter(columnId);
      }
      setAlertType('success');
      setCustomAlertMessage('Column configuration deleted successfully');
      setShowAlert(true);
      setRefreshColumnsBar(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting column configuration:', error);
      setAlertType('error');
      setCustomAlertMessage('Error deleting column configuration');
      setShowAlert(true);
    }
  };

  const getSavedColumnSelections = () => {
    const savedSelections = [] as Array<{
      key: string;
      name: string;
      columnVisibilityModel: ColumnVisibility;
      columnOrder: string[];
      savedAt: string;
      searchContext: {
        view: string;
        tab: string;
        isEvidence: boolean;
        searchItems: Array<{ id: string; name: string }>;
      };
    }>;
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith('columnSelection_')) {
        try {
          const selection = JSON.parse(localStorage.getItem(key) || '');
          savedSelections.push({
            key,
            ...selection,
          });
        } catch (error) {
          console.error('Error parsing saved column selection:', error);
        }
      }
    });

    return savedSelections.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const handleSaveScore = () => {
    if (!isAuthenticated) {
      setAlertType('error');
      setCustomAlertMessage('You must be logged in to save scores');
      setShowAlert(true);
      return;
    }

    if (!sourceWeights || sourceWeights.length === 0) {
      setAlertType('warning');
      setCustomAlertMessage('No custom scores to save');
      setShowAlert(true);
      return;
    }

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Score';

    const defaultScoreName = `${searchItemsText} - Score (${new Date().toLocaleDateString()})`;
    setScoreName(defaultScoreName);
    setShowSaveScoreModal(true);
    setShowSaveDropdown(false);
  };

  const handleConfirmSaveScore = async () => {
    if (!scoreName.trim() || isSavingScore) return;

    setIsSavingScore(true);

    try {
      try {
        const { saveScore } = await import('../../../../../services/savedFiltersService');

        await saveScore(scoreName.trim(), sourceWeights, {
          view,
          tab,
          source,
          searchItems: searchItems?.map(item => ({ id: item.id, name: item.name })),
        });
      } catch (dbError) {
        console.warn('⚠️ DynamoDB save failed, falling back to localStorage:', dbError);

        const timestamp = new Date().getTime();
        const scoreKey = `savedScore_${timestamp}`;

        const scoreData = {
          name: scoreName.trim(),
          sourceWeights,
          savedAt: new Date().toISOString(),
          searchContext: {
            view,
            tab,
            source,
            searchItems: searchItems?.map(item => ({ id: item.id, name: item.name })),
          },
        };

        localStorage.setItem(scoreKey, JSON.stringify(scoreData));
      }

      setAlertType('success');
      setCustomAlertMessage('Score saved successfully!');
      setShowAlert(true);
      setShowSaveScoreModal(false);
      setScoreName('');

      if (onScoreSaved) {
        onScoreSaved();
      }

      setTimeout(() => {
        setIsSavingScore(false);
      }, 100);
    } catch (error) {
      console.error('Error saving score:', error);
      setAlertType('error');
      setCustomAlertMessage('Error saving score. Please try again.');
      setShowAlert(true);
      setIsSavingScore(false);
    }
  };

  const handleLoadColumnSelection = (selection: any) => {
    if (selection.columnVisibilityModel) {
      setColumnVisibilityModel(selection.columnVisibilityModel);
    }
    if (selection.columnOrder) {
      setColumnOrder(selection.columnOrder);
    }

    setAlertType('success');
    setShowAlert(true);
  };

  const performDownloadWithFilters = async (extension: ExtensionFile) => {
    try {
      setAlertType('warning');
      setShowAlert(true);
      dispatch({ type: 'SET_LOADING', payload: true });

      const filterValue = getFilterValueRef.current ? getFilterValueRef.current() : [];

      await fetchDownloadFiltersFile({
        extension: extension.value,
        columnVisibilityModel: columnVisibilityModel as ColumnVisibility,
        view,
        source,
        tab,
        searchItems,
        titleGrid,
        isAuthenticated,
        authorization,
        isEvidence,
        isMapping,
        mappingContent,
        filters: buildFiltersObject(filterValue),
        filterValue,
        totalResults,
      });

      setAlertType('success');
      setShowAlert(true);
    } catch (error) {
      setAlertType('error');
      setShowAlert(true);
      console.error('Error downloading file:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleDownloadFilesWithFilters = async (extension: ExtensionFile) => {
    setPendingDownloadExt(extension);

    if (searchItems && searchItems.length > 50) {
      setCustomAlertMessage('Cannot download more than 50 search items. Please reduce your selection.');
      setAlertType('error');
      setShowAlert(true);
      return;
    }

    if (totalResults > 55000) {
      setShowDownloadConfirm(true);
      setPendingDownloadExt(extension);
      return;
    }

    await performDownloadWithFilters(extension);
  };

  const titleGrid = isEvidence ? 'Evidence' : 'Summary';

  // Memoized columns based on columnVisibilityModel
  const columns = useMemo(() => {
    if (!columnVisibilityModel) return null;

    const originalModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
    if (!originalModel) return null;

    const orderedKeys = Object.keys(originalModel);
    const visibleKeys = orderedKeys.filter(key => columnVisibilityModel[key] === true);

    const allColumns = Columns();
    const orderedColumns = [];

    visibleKeys.forEach(key => {
      const column = allColumns.find(
        col =>
          col.name === key &&
          columnVisibilityModel[col.name] === true &&
          (col.show === undefined || col.show === true) &&
          (col.name !== 'sourceScore' || sourceWeights.length > 0),
      );

      if (column) {
        orderedColumns.push(column);
      }
    });

    return orderedColumns;
  }, [columnVisibilityModel, sourceWeights, tab, view, isEvidence]);

  useEffect(() => {
    const searchAndMappingItems = searchItems ? searchItems.concat(mappingContent?.mappingItems || []) : [];
    if (isInfoSidebarOpen && searchItems && !searchItems.find(item => item.id === selectedInfoItem.id)) {
      if (!searchAndMappingItems.find(item => item.id === selectedInfoItem.id)) {
        setIsInfoSidebarOpen(false);
      }
    }
  }, [searchItems, selectedInfoItem, mappingContent?.mappingItems, isInfoSidebarOpen, setIsInfoSidebarOpen]);

  const activeFilters = searchDataToSave?.columnFilters?.filter(f => f.value && f.value.length !== 0) || [];

  if (isAcademic && isAuthenticated && tab === 'CHEMICALS') {
    return (
      <ErrorMessage
        title="This content is not available with your current plan"
        subtitle={
          <div className="text-center">
            <p>Upgrade your plan to access this feature.</p>
            <span>
              <button className="btn-gradient-pink">
                <a href="/plans" className="text-white">
                  Upgrade your plan
                </a>
              </button>
            </span>
          </div>
        }
      />
    );
  }

  return !columns ? null : (
    <>
      {showAlert && (
        <div
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert
            message={alertMessage() as string}
            type={alertType}
            onClose={() => {
              setShowAlert(false);
              setCustomAlertMessage(null);
            }}
          />
        </div>
      )}
      {showDownloadConfirm && (
        <SharedDialog
          isOpen={showDownloadConfirm}
          onClose={() => setShowDownloadConfirm(false)}
          title="Large file download warning"
          description="The file you are requesting contains more than 55,000 records. Downloads are limited to this number. We suggest using the REST API for this request."
          confirmText="Yes"
          cancelText="No"
          hasConfirmBtn
          onConfirm={async () => {
            if (pendingDownloadExt) {
              await performDownloadWithFilters(pendingDownloadExt);
              setPendingDownloadExt(null);
            }
            setShowDownloadConfirm(false);
          }}
          data-testid="modal"
        >
          <p className="font-medium text-gray-900 mt-2">Do you want to proceed?</p>
        </SharedDialog>
      )}

      {/* Columns modal */}
      {showSaveColumnsModal && (
        <SharedDialog
          isOpen={showSaveColumnsModal}
          onClose={() => {
            setShowSaveColumnsModal(false);
            setColumnDataToSave(null);
            setColumnSelectionName('');
          }}
          title="Save Column Selection"
          description="Give your column selection a name to save it for later use."
          confirmText={isSavingColumns ? 'Saving...' : 'Save'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveColumnSelection}
          data-testid="save-columns-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="columnSelectionName" className="form-label font-medium">
                Column Selection Name:
              </label>
              <input
                id="columnSelectionName"
                type="text"
                className="form-control"
                value={columnSelectionName}
                onChange={e => setColumnSelectionName(e.target.value)}
                placeholder="Enter a name for this column selection"
                maxLength={100}
                disabled={isSavingColumns}
              />
              {!columnSelectionName.trim() && (
                <small className="text-danger">Please enter a name for the column selection</small>
              )}
            </div>

            {columnDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Selection Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-12">
                      <small className="text-muted">View:</small> <strong>{columnDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{columnDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{columnDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                      {columnDataToSave.searchItems && columnDataToSave.searchItems.length > 0 && (
                        <>
                          <br />
                          <small className="text-muted">Search Items:</small>{' '}
                          <strong>
                            {columnDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                          </strong>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">Visible Columns:</small>
                    <br />
                    <small>
                      {Object.entries(columnDataToSave.columnVisibilityModel || {})
                        .filter(([_, visible]) => visible)
                        .map(([col]) => col)
                        .join(', ')}
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}

      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <SharedDialog
          isOpen={showSaveSearchModal}
          onClose={() => {
            setShowSaveSearchModal(false);
            setSearchDataToSave(null);
            setSearchName('');
          }}
          title="Save Search"
          description="Review and customize your search before saving it to favorites."
          confirmText={isSavingSearch ? 'Saving...' : 'Save Search'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveSearch}
          data-testid="save-search-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="searchName" className="form-label font-medium">
                Search Name:
              </label>
              <input
                id="searchName"
                type="text"
                className="form-control"
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                placeholder="Enter a name for this search"
                maxLength={100}
                disabled={isSavingSearch}
              />
              {!searchName.trim() && (
                <small className="text-danger">Please enter a name for the search</small>
              )}
            </div>

            {searchDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Search Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">View:</small> <strong>{searchDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{searchDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Source:</small> <strong>{searchDataToSave.source}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{searchDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Search Items:</small>
                      <br />
                      {searchDataToSave.searchItems.length > 0 ? (
                        <small>
                          {searchDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted">No specific items</small>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    <small className="text-muted">Applied Column Filters:</small>
                    <br />
                    {activeFilters.length > 0 ? (
                      <small>
                        {activeFilters
                          .map(
                            f =>
                              `${f.label || f.name}: ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`,
                          )
                          .join(', ')}
                      </small>
                    ) : (
                      <small className="text-muted">No column filters applied</small>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}
      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <SharedDialog
          isOpen={showSaveSearchModal}
          onClose={() => {
            setShowSaveSearchModal(false);
            setSearchDataToSave(null);
            setSearchName('');
          }}
          title="Save Search"
          description="Review and customize your search before saving it to favorites."
          confirmText={isSavingSearch ? 'Saving...' : 'Save Search'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveSearch}
          data-testid="save-search-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="searchName" className="form-label font-medium">
                Search Name:
              </label>
              <input
                id="searchName"
                type="text"
                className="form-control"
                value={searchName}
                onChange={e => setSearchName(e.target.value)}
                placeholder="Enter a name for this search"
                maxLength={100}
                disabled={isSavingSearch}
              />
              {!searchName.trim() && (
                <small className="text-danger">Please enter a name for the search</small>
              )}
            </div>

            {searchDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Search Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">View:</small> <strong>{searchDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{searchDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Source:</small> <strong>{searchDataToSave.source}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{searchDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Search Items:</small>
                      <br />
                      {searchDataToSave.searchItems.length > 0 ? (
                        <small>
                          {searchDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                        </small>
                      ) : (
                        <small className="text-muted">No specific items</small>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    <small className="text-muted">Applied Column Filters:</small>
                    <br />
                    {activeFilters.length > 0 ? (
                      <small>
                        {activeFilters
                          .map(
                            f =>
                              `${f.label || f.name}: ${Array.isArray(f.value) ? f.value.join(', ') : f.value}`,
                          )
                          .join(', ')}
                      </small>
                    ) : (
                      <small className="text-muted">No column filters applied</small>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}
      {/* Columns modal */}
      {showSaveColumnsModal && (
        <SharedDialog
          isOpen={showSaveColumnsModal}
          onClose={() => {
            setShowSaveColumnsModal(false);
            setColumnDataToSave(null);
            setColumnSelectionName('');
          }}
          title="Save Column Selection"
          description="Give your column selection a name to save it for later use."
          confirmText={isSavingColumns ? 'Saving...' : 'Save'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveColumnSelection}
          data-testid="save-columns-modal"
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="columnSelectionName" className="form-label font-medium">
                Column Selection Name:
              </label>
              <input
                id="columnSelectionName"
                type="text"
                className="form-control"
                value={columnSelectionName}
                onChange={e => setColumnSelectionName(e.target.value)}
                placeholder="Enter a name for this column selection"
                maxLength={100}
                disabled={isSavingColumns}
              />
              {!columnSelectionName.trim() && (
                <small className="text-danger">Please enter a name for the column selection</small>
              )}
            </div>

            {columnDataToSave && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Selection Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="row">
                    <div className="col-md-12">
                      <small className="text-muted">View:</small> <strong>{columnDataToSave.view}</strong>
                      <br />
                      <small className="text-muted">Tab:</small> <strong>{columnDataToSave.tab}</strong>
                      <br />
                      <small className="text-muted">Type:</small>{' '}
                      <strong>{columnDataToSave.isEvidence ? 'Evidence' : 'Summary'}</strong>
                      {columnDataToSave.searchItems && columnDataToSave.searchItems.length > 0 && (
                        <>
                          <br />
                          <small className="text-muted">Search Items:</small>{' '}
                          <strong>
                            {columnDataToSave.searchItems.map(item => item.name || item.id).join(', ')}
                          </strong>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">Visible Columns:</small>
                    <br />
                    <small>
                      {Object.entries(columnDataToSave.columnVisibilityModel || {})
                        .filter(([_, visible]) => visible)
                        .map(([col]) => col)
                        .join(', ')}
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}

      {/* Save Score Modal */}
      {showSaveScoreModal && (
        <SharedDialog
          isOpen={showSaveScoreModal}
          onClose={() => {
            setShowSaveScoreModal(false);
            setScoreName('');
          }}
          title="Save Score Weights"
          description="Give your custom score weights a name to save them for later use."
          confirmText={isSavingScore ? 'Saving...' : 'Save'}
          cancelText="Cancel"
          onConfirm={handleConfirmSaveScore}
          hasConfirmBtn
        >
          <div className="mt-4">
            <div className="mb-3">
              <label htmlFor="scoreName" className="form-label font-medium">
                Score Name:
              </label>
              <input
                id="scoreName"
                type="text"
                className="form-control"
                value={scoreName}
                onChange={e => setScoreName(e.target.value)}
                placeholder="Enter a name for this score configuration"
                maxLength={100}
                disabled={isSavingScore}
              />
              {!scoreName.trim() && <small className="text-danger">Please enter a name for the score</small>}
            </div>

            {sourceWeights && sourceWeights.length > 0 && (
              <div className="mt-3">
                <h6 className="font-medium mb-2">Score Details:</h6>
                <div className="bg-light p-3 rounded">
                  <div className="mb-2">
                    <small className="text-muted">View:</small> <strong>{view}</strong>
                    <br />
                    <small className="text-muted">Tab:</small> <strong>{tab}</strong>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">Source Weights:</small>
                    <br />
                    {sourceWeights.map((sw, idx) => (
                      <div key={idx} className="mt-1">
                        <small>
                          <strong>{sw.name.replace('_', ' ')}</strong>: {sw.score.toFixed(2)}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SharedDialog>
      )}

      <div className="Cabtabla bg-white mt-3">
        {searchItems && (
          <>
            <div className="row">
              <RenderInfoLogic
                searchItems={searchItems}
                selectedInfoItem={selectedInfoItem}
                setSearchItems={setSearchItems}
                isMapping={isMapping}
              />
              <div className="col-6 text-right">
                <div className="row justify-content-end items-center">
                  <SharedButton
                    onClick={() => handleOpenMenus('columns')}
                    arrow={isColumnSelectOpen ? 'up' : 'down'}
                    className="mt-2"
                  >
                    <ViewColumnsIcon className="w-5 mr-2" />
                    Select Columns
                  </SharedButton>
                  <SharedButton
                    onClick={() => handleOpenMenus('source')}
                    arrow={sourceOpen ? 'up' : 'down'}
                    className="mt-2"
                  >
                    Source: {sourceWeights.length > 1 ? 'CUSTOM' : source.replace('_', ' ')}
                  </SharedButton>
                </div>

                <div className="row justify-content-end items-center">
                  {isAuthenticated && !isAcademic && !isTrial && process.env.REACT_APP_SHOW_SAVE_SEARCHES === 'true' && (
                    <div style={{ position: 'relative' }} ref={saveDropdownRef}>
                      <SharedButton
                        onClick={() => setShowSaveDropdown(!showSaveDropdown)}
                        disabled={isSavingSearch || isSavingColumns}
                        className="mt-2"
                      >
                        <BookmarkIcon className="w-5 mr-2" />
                        Save
                        <ChevronDownIcon
                          className="w-4 ml-2 transition-transform duration-200"
                          style={{ transform: showSaveDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        />
                      </SharedButton>
                      {showSaveDropdown &&
                        (() => {
                          const columnFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];
                          const contextParams = ['view', 'tab', 'source', 'idents'];
                          const activeFilters = columnFilters.filter(
                            f =>
                              !contextParams.includes(f.name) &&
                              f.value &&
                              f.value !== '' &&
                              !(Array.isArray(f.value) && f.value.length === 0),
                          );
                          const hasFilters = activeFilters.length > 0;
                          const hasSearchItems = searchItems && searchItems.length > 0;

                          const defaultModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

                          const hasCustomVisibility =
                            columnVisibilityModel &&
                            defaultModel &&
                            JSON.stringify(columnVisibilityModel) !== JSON.stringify(defaultModel);

                          const defaultColumnOrder = defaultModel 
                            ? Object.keys(defaultModel).filter(key => defaultModel[key] === true)
                            : [];
                          
                          const hasCustomOrder =
                            columnOrder &&
                            columnOrder.length > 0 &&
                            JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

                          const hasCustomColumns = hasCustomVisibility || hasCustomOrder;

                          return (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '4px',
                                background: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000,
                                minWidth: '240px',
                                overflow: 'hidden',
                              }}
                            >
                              <button
                                onClick={handleSaveComplete}
                                disabled={!hasSearchItems && !hasFilters && !hasCustomColumns}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  border: 'none',
                                  background: 'white',
                                  textAlign: 'left',
                                  cursor:
                                    !hasSearchItems && !hasFilters && !hasCustomColumns
                                      ? 'not-allowed'
                                      : 'pointer',
                                  fontSize: '14px',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  opacity: !hasSearchItems && !hasFilters && !hasCustomColumns ? 0.5 : 1,
                                }}
                                onMouseEnter={e => {
                                  if (hasSearchItems || hasFilters || hasCustomColumns) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }
                                }}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                              >
                                <RectangleStackIcon className="w-5 h-5" />
                                <div>
                                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                    Complete Search
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {!hasSearchItems && !hasFilters && !hasCustomColumns
                                      ? 'No search to save'
                                      : 'Search + filters + columns + order'}
                                  </div>
                                </div>
                              </button>
                              <div style={{ borderTop: '1px solid #eee' }} />
                              <button
                                onClick={handleSaveSearch}
                                disabled={!hasFilters}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  border: 'none',
                                  background: 'white',
                                  textAlign: 'left',
                                  cursor: !hasFilters ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  opacity: !hasFilters ? 0.5 : 1,
                                }}
                                onMouseEnter={e => {
                                  if (hasFilters) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }
                                }}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                              >
                                <FunnelIcon className="w-5 h-5" />
                                <div>
                                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>Filters Only</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {!hasFilters ? 'No filters applied' : 'Save applied filters'}
                                  </div>
                                </div>
                              </button>
                              <div style={{ borderTop: '1px solid #eee' }} />
                              <button
                                onClick={handleSaveColumnSelection}
                                disabled={!hasCustomColumns}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  border: 'none',
                                  background: 'white',
                                  textAlign: 'left',
                                  cursor: !hasCustomColumns ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  opacity: !hasCustomColumns ? 0.5 : 1,
                                }}
                                onMouseEnter={e => {
                                  if (hasCustomColumns) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }
                                }}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                              >
                                <TableCellsIcon className="w-5 h-5" />
                                <div>
                                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>Columns Only</div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {!hasCustomColumns ? 'No columns customized' : 'Visibility + order'}
                                  </div>
                                </div>
                              </button>
                              {['GDA', 'VDA'].includes(tab) && sourceWeights.length > 0 && (
                                <>
                                  <div style={{ borderTop: '1px solid #eee' }} />
                                  <button
                                    onClick={handleSaveScore}
                                    style={{
                                      width: '100%',
                                      padding: '12px 16px',
                                      border: 'none',
                                      background: 'white',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      transition: 'background-color 0.2s',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                                    }}
                                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                      />
                                    </svg>
                                    <div>
                                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                        Score Weights
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#666' }}>
                                        Save custom source scores
                                      </div>
                                    </div>
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })()}
                    </div>
                  )}
                  {(() => {
                    const defaultModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

                    const hasCustomVisibility =
                      columnVisibilityModel &&
                      defaultModel &&
                      JSON.stringify(columnVisibilityModel) !== JSON.stringify(defaultModel);

                    const defaultColumnOrder = defaultModel 
                      ? Object.keys(defaultModel).filter(key => defaultModel[key] === true)
                      : [];
                    
                    const hasCustomOrder =
                      columnOrder &&
                      columnOrder.length > 0 &&
                      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

                    const hasCustomColumns = hasCustomVisibility || hasCustomOrder;

                    return hasCustomColumns ? (
                      <BaseTooltip content="Reset columns to default view" position="bottom">
                        <SharedButton onClick={handleResetToDefault} className="mt-2" variant="secondary">
                          Reset Columns
                        </SharedButton>
                      </BaseTooltip>
                    ) : null;
                  })()}
                </div>

                <div className="justify-content-end mt-2">
                  {view !== 'ALL' &&
                    DOWNLOAD_EXTENSIONS.map((extension, index) => (
                      <>
                        <BaseTooltip
                          key={extension.value || index}
                          content={`This will download a zip with search info a result`}
                          position="bottom"
                          contentClassName="max-w-xl"
                          style={{ whiteSpace: 'break-spaces', width: '15em' }}
                        >
                          <SharedButton onClick={() => handleDownloadFilesWithFilters(extension)}>
                            {extension.label}
                            <ArrowDownTrayIcon className="w-4 ml-2" />
                          </SharedButton>
                        </BaseTooltip>
                      </>
                    ))}
                </div>
              </div>
              <div>
                {sourceOpen && (
                  <RenderSourcesLogic isScoreSource={false} selectedSourceWeights={sourceWeights} />
                )}
                {isAcademic && tab !== 'CHEMICALS' && sourceOpen && (
                  <ErrorMessage
                    title=""
                    subtitle={
                      <p style={{ fontSize: '18px', color: 'rgb(190,0,128)' }}>
                        You are viewing a subset of the available sources. Do you want to access all sources?
                        <a href="/academic-plans" style={{ color: '#0d1661' }}>
                          {' '}
                          Upgrade your account{' '}
                        </a>
                      </p>
                    }
                  />
                )}
              </div>
            </div>
            {isColumnSelectOpen && (
              <TableColumnsMenu
                view={view}
                tab={tab}
                setIsColumnSelectOpen={setIsColumnSelectOpen}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
              />
            )}
            {showSavedColumns && (
              <div className="mt-3 p-3 border rounded bg-light">
                <h6>Saved Column Selections</h6>
                {(() => {
                  const savedSelections = getSavedColumnSelections();
                  if (savedSelections.length === 0) {
                    return <p className="text-muted">No saved column selections found.</p>;
                  }
                  return (
                    <div className="row">
                      {savedSelections.map((selection, index) => (
                        <div key={selection.key} className="col-md-6 mb-2">
                          <div className="columns-card p-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{selection.name}</strong>
                                <br />{' '}
                                <small className="text-muted">
                                  {selection.searchContext?.view} - {selection.searchContext?.tab}
                                  {selection.searchContext?.isEvidence ? ' (Evidence)' : ' (Summary)'} -{' '}
                                  Saved: {new Date(selection.savedAt).toLocaleDateString()}
                                </small>
                              </div>
                              <div>
                                <SharedButton
                                  size="xs"
                                  onClick={() => handleLoadColumnSelection(selection)}
                                  className="mr-1"
                                >
                                  Load
                                </SharedButton>
                                <SharedButton
                                  size="xs"
                                  variant="danger"
                                  onClick={() => {
                                    localStorage.removeItem(selection.key);
                                    setShowSavedColumns(false);
                                    setTimeout(() => setShowSavedColumns(true), 100);
                                  }}
                                >
                                  Delete
                                </SharedButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="titEtiqueta">
              <span>{titleGrid}</span>
            </div>
          </>
        )}
      </div>
      {isEvidence && (
        <div className="flex justify-between">
          {EVIDENCE_LEGEND}
          {areEvidenceSelectedRows && SHOW_SUMMARIZE && (
            <div>
              <SharedButton onClick={handleEvidenceSummClick}>Summarize</SharedButton>
            </div>
          )}
        </div>
      )}

      {/* Saved Columns Bar */}
      <SavedColumnsBar
        key={refreshColumnsBar}
        view={view}
        tab={tab}
        isEvidence={isEvidence}
        onApplyColumns={handleApplySavedColumns}
        onDeleteColumn={handleDeleteSavedColumn}
      />

      {isAcademic && !sourceCurated.includes(source) && (
        <div className="text-center text-gray-500 p-6">
          <h3>Your free academic plan includes curated sources only.</h3>
          <h5>Select your available sources here.</h5>
        </div>
      )}

      {isInvalidSource ? (
        <div className="text-center text-gray-500 italic p-6">
          <strong>{source}</strong> is not a source for <strong>{tab}</strong>
        </div>
      ) : (
        <Grid
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          view={view}
          source={source}
          tab={tab}
          searchItems={searchItems}
          isEvidence={isEvidence}
          isMapping={isMapping}
          setTotalResults={setTotalResults}
          onData={handleDataFromChild}
          getFilterValueRef={getFilterValueRef}
          defaultFilterValue={defaultFilterValue}
          sourceWeights={sourceWeights}
        />
      )}
    </>
  );
}

export default ManageGrid;
